import { relationshipKeys, type RelationshipProposal } from '@taler/api-client'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed, type ComputedRef, reactive } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'

export interface UseProposals {
  readonly proposals: ComputedRef<readonly RelationshipProposal[]>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  isBusy(proposalId: string): boolean
  accept(proposal: RelationshipProposal): Promise<void>
  decline(proposal: RelationshipProposal): Promise<void>
  retry(): void
}

export function useProposals(ledgerId: () => string | undefined): UseProposals {
  const api = useApi()
  const queryClient = useQueryClient()
  const busy = reactive(new Set<string>())

  const query = useQuery({
    queryKey: computed(() => relationshipKeys.proposals(ledgerId() ?? '')),
    queryFn: ({ signal }) => api.relationships.listProposals(ledgerId() ?? '', signal),
    enabled: computed(() => ledgerId() !== undefined),
    retry: false,
  })

  async function invalidate(proposal: RelationshipProposal): Promise<void> {
    const keys = relationshipKeys.invalidatedByWrite(proposal.proposingLedgerId, proposal.targetLedgerId)
    await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
  }

  return {
    proposals: computed(() => query.data.value ?? []),
    isLoading: computed(() => query.isPending.value),
    error: computed(() => query.error.value),
    retrying: retrying(query),
    isBusy: (proposalId) => busy.has(proposalId),
    retry: () => {
      void query.refetch()
    },
    async accept(proposal) {
      busy.add(proposal.id)
      try {
        await api.relationships.accept(proposal.targetLedgerId, proposal.id)
        await invalidate(proposal)
      } finally {
        busy.delete(proposal.id)
      }
    },
    async decline(proposal) {
      busy.add(proposal.id)
      try {
        await api.relationships.decline(proposal.targetLedgerId, proposal.id)
        await invalidate(proposal)
      } finally {
        busy.delete(proposal.id)
      }
    },
  }
}
